import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { diffLines, diffWords } from 'diff';
import type { HarnessEvent } from '@xiaotianshu/shared/harness';
import {
  answerApproval,
  closeSession,
  createHarnessSession,
  fetchBooks,
  fetchChapterContent,
  fetchChapters,
  fetchModels,
  fetchVolumes,
  openEventStream,
  sendSessionMessage,
  type BookRow,
  type ChapterRow,
  type ModelRow,
  type VolumeRow,
} from '../../ai/harnessClient';
import './harnessDemo.css';

// ───────────────────────── 类型 ─────────────────────────

interface ApprovalState {
  requestId: string;
  decision?: 'allow' | 'deny' | 'timeout';
}

interface ToolCardState {
  callId: string;
  name: string;
  argsText: string;
  args: unknown;
  result?: { success: boolean; content: string };
  approval?: ApprovalState;
  open: boolean;
}

type ChatItem =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string; reasoning: string; thinkingOpen: boolean; done: boolean }
  | { kind: 'tool'; tool: ToolCardState }
  | { kind: 'system'; text: string };

interface ExecuteAction {
  action: string;
  target: string;
  targetId?: string;
  data?: Record<string, unknown>;
}

interface DiffData {
  chapterTitle: string;
  current: string;
  next: string;
}

// ───────────────────────── 工具参数解析与渲染 ─────────────────────────

function labelForTarget(target: string): string {
  const map: Record<string, string> = {
    character: '人物', faction: '势力', setting: '世界设定', item: '物品',
    volume: '分卷', outline: '故事大纲', chapter: '章节', chapter_content: '章节内容',
  };
  return map[target] ?? target;
}

function parseActions(args: unknown): { actions: ExecuteAction[]; atomic: boolean; description?: string } {
  const a = (args ?? {}) as { actions?: unknown; atomic?: boolean; description?: string };
  let actions = a.actions;
  if (actions && !Array.isArray(actions) && typeof actions === 'object') {
    const single = actions as { action?: string };
    actions = single.action ? [actions] : [];
  }
  return {
    actions: Array.isArray(actions) ? actions as ExecuteAction[] : [],
    atomic: a.atomic ?? false,
    description: a.description,
  };
}

function ActionBrief({ action }: { action: ExecuteAction }) {
  const data = action.data ?? {};
  if (action.action === 'create') {
    const name = data.name ?? data.title ?? '';
    return (
      <span>
        创建<strong>{labelForTarget(action.target)}</strong>
        {name ? <>「{String(name)}」</> : null}
      </span>
    );
  }
  if (action.action === 'update') {
    const dataKeys = Object.keys(data);
    const brief = dataKeys.slice(0, 2).map((k) => `${k}=${String(data[k] ?? '').slice(0, 24)}`).join(', ');
    return (
      <span>
        修改<strong>{labelForTarget(action.target)}</strong>
        {action.targetId ? <>（{action.targetId.slice(0, 8)}…）</> : null}
        {brief ? <>：{brief}{dataKeys.length > 2 ? ' …' : ''}</> : null}
      </span>
    );
  }
  return (
    <span>
      删除<strong>{labelForTarget(action.target)}</strong>
      {action.targetId ? <>（{action.targetId.slice(0, 8)}…）</> : null}
    </span>
  );
}

/** 相似度：两行共同文本长度占比（≥0.45 才适合词级细化，否则整行红/绿避免乱拼） */
function lineSimilarity(a: string, b: string): number {
  if (a.length + b.length === 0) return 1;
  const chunks = diffWords(a, b);
  const same = chunks.filter((c) => !c.added && !c.removed).reduce((s, c) => s + c.value.length, 0);
  return (2 * same) / (a.length + b.length);
}

/** 行级 + 词级 diff 渲染（块级对齐：内容逐字来自某一侧，绝不遗失；不相关段落整行红/绿） */
function DiffView({ current, next }: { current: string; next: string }) {
  const render = useMemo(() => {
    const parts = diffLines(current, next);
    const out: Array<{ type: 'same' | 'add' | 'del'; text: string }> = [];

    const splitLines = (value: string): string[] => {
      const lines = value.replace(/\n$/, '').split('\n');
      return lines.length > 1 && lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines;
    };

    let i = 0;
    let pendingRemoved: string[] | null = null;
    const flushRemoved = (): void => {
      if (pendingRemoved) {
        for (const line of pendingRemoved) out.push({ type: 'del', text: line });
        pendingRemoved = null;
      }
    };

    while (i < parts.length) {
      const part = parts[i];
      if (part.added) {
        const addedLines = splitLines(part.value);
        if (pendingRemoved && pendingRemoved.length === addedLines.length) {
          for (let li = 0; li < addedLines.length; li++) {
            const from = pendingRemoved[li];
            const to = addedLines[li];
            if (lineSimilarity(from, to) >= 0.45) {
              const chunks = diffWords(from, to);
              for (const chunk of chunks) {
                if (chunk.added) out.push({ type: 'add', text: chunk.value });
                else if (chunk.removed) out.push({ type: 'del', text: chunk.value });
                else out.push({ type: 'same', text: chunk.value });
              }
            } else {
              out.push({ type: 'del', text: from });
              out.push({ type: 'add', text: to });
            }
          }
        } else {
          flushRemoved();
          for (const line of addedLines) out.push({ type: 'add', text: line });
        }
        pendingRemoved = null;
        i++;
        continue;
      }
      if (part.removed) {
        flushRemoved();
        pendingRemoved = splitLines(part.value);
        i++;
        continue;
      }
      flushRemoved();
      for (const line of splitLines(part.value)) out.push({ type: 'same', text: line });
      i++;
    }
    flushRemoved();
    return out;
  }, [current, next]);

  return (
    <pre className="hdiff">
      {render.map((seg, idx) => (
        <span key={idx} className={`hdiff-line hdiff-${seg.type}`}>{seg.text}</span>
      ))}
    </pre>
  );
}

function parseJsonSafe(text: string | undefined): unknown {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function prettyPrint(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function toolLabel(name: string): string {
  if (name === 'query') return '📖 query 查询创作数据';
  if (name === 'execute') return '⚡ execute 执行变更';
  if (name === 'help') return '📚 help 工具文档';
  return `🛠 ${name}`;
}

// ───────────────────────── 工具卡片组件 ─────────────────────────

function ExecuteDetail({ tool }: { tool: ToolCardState }) {
  const parsed = useMemo(() => parseActions(tool.args), [tool.args]);
  const [diffs, setDiffs] = useState<Record<string, DiffData>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const entries: Array<[string, DiffData]> = [];
      for (const action of parsed.actions) {
        if (action.target === 'chapter_content' && action.targetId) {
          try {
            const [current, meta] = await Promise.all([
              fetchChapterContent(action.targetId),
              fetch(`/api/chapters/${action.targetId}`, { credentials: 'include' })
                .then((r): Promise<unknown> => r.json())
                .catch((): null => null),
            ]);
            const nextContent = String(action.data?.content ?? '');
            entries.push([
              action.targetId,
              { chapterTitle: (meta as { title?: string } | null)?.title ?? action.targetId.slice(0, 8), current, next: nextContent },
            ]);
          } catch { /* ignore */ }
        }
      }
      if (!cancelled && entries.length > 0) setDiffs(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [parsed.actions]);

  return (
    <>
      <ul className="hactions">
        {parsed.actions.map((action, idx) => <li key={idx}><ActionBrief action={action} /></li>)}
      </ul>
      {parsed.description ? <div className="hdesc">说明：{parsed.description}</div> : null}
      {parsed.atomic ? <div className="hdesc">原子模式：任一失败即终止</div> : null}
      {Object.entries(diffs).map(([id, d]) => (
        <div key={id} className="hdiff-block">
          <div className="hdiff-title">章节《{d.chapterTitle}》正文改动（当前 → 新）</div>
          <DiffView current={d.current} next={d.next} />
        </div>
      ))}
    </>
  );
}

function ApprovalButtons({ sessionId, tool }: { sessionId: string; tool: ToolCardState }) {
  const approval = tool.approval;
  const [busy, setBusy] = useState(false);

  if (!approval) {
    return <div className="happroval"><span className="happroval-tip">⏳ 等待审批请求…</span></div>;
  }
  if (approval.decision === 'allow') {
    return <div className="happroval happroval-done">✅ 已批准，正在写入…</div>;
  }
  if (approval.decision === 'deny') {
    return <div className="happroval happroval-done">⛔ 已拒绝</div>;
  }
  if (approval.decision === 'timeout') {
    return <div className="happroval happroval-done">⏰ 审批超时已拒绝</div>;
  }

  return (
    <div className="happroval">
      <span className="happroval-tip">该写入需要你确认：</span>
      <button
        className="hbtn hbtn-allow"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void answerApproval(sessionId, approval.requestId, 'allow').catch(() => setBusy(false));
        }}
      >
        ✓ 批准
      </button>
      <button
        className="hbtn hbtn-deny"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void answerApproval(sessionId, approval.requestId, 'deny').catch(() => setBusy(false));
        }}
      >
        ✗ 拒绝
      </button>
    </div>
  );
}

function ToolCard({ item, sessionId, onToggle }: {
  item: Extract<ChatItem, { kind: 'tool' }>;
  sessionId: string;
  onToggle: () => void;
}) {
  const tool = item.tool;
  const isExecute = tool.name === 'execute';

  return (
    <div className={`hcard${isExecute ? ' hcard-execute' : ''}`}>
      <div className="hcard-head" onClick={onToggle}>
        <span className="hcard-icon">{isExecute ? '⚡' : tool.name === 'query' ? '📖' : '📚'}</span>
        <span className="hcard-title">{toolLabel(tool.name)}</span>
        <span className="hcard-chevron">{tool.open ? '▾' : '▸'}</span>
      </div>

      {tool.open ? (
        <div className="hcard-body">
          {isExecute ? (
            <ExecuteDetail tool={tool} />
          ) : (
            <pre className="hargs">{prettyPrint(tool.args)}</pre>
          )}
          {tool.result ? (
            <div className="hresult-wrap">
              <div className={`hresult${tool.result.success ? ' hresult-ok' : ' hresult-err'}`}>
                {tool.result.success ? '✓' : '✗'} {tool.result.content.slice(0, 500)}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {isExecute ? <ApprovalButtons sessionId={sessionId} tool={tool} /> : null}
    </div>
  );
}

// ───────────────────────── 主页面 ─────────────────────────

export function HarnessDemo() {
  const [books, setBooks] = useState<BookRow[]>([]);
  const [bookId, setBookId] = useState('');
  const [volumes, setVolumes] = useState<VolumeRow[]>([]);
  const [volumeId, setVolumeId] = useState('');
  const [chapters, setChapters] = useState<ChapterRow[]>([]);
  const [chapterId, setChapterId] = useState('');
  const [provider, setProvider] = useState<'deepseek' | 'openrouter'>('deepseek');
  const [models, setModels] = useState<ModelRow[]>([]);
  const [model, setModel] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [running, setRunning] = useState(false);
  const [streamState, setStreamState] = useState<'idle' | 'open' | 'closed'>('idle');
  const [items, setItems] = useState<ChatItem[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');

  // 初始加载书籍
  useEffect(() => {
    fetchBooks()
      .then((list) => { setBooks(list); if (list[0]) setBookId(list[0].id); })
      .catch((e) => setLoadError(e instanceof Error ? e.message : '加载作品失败'));
  }, []);

  // 书 → 卷
  useEffect(() => {
    if (!bookId) return;
    fetchVolumes(bookId)
      .then((list) => { setVolumes(list); setVolumeId(list[0]?.id ?? ''); })
      .catch(() => setVolumes([]));
  }, [bookId]);

  // 卷 → 章
  useEffect(() => {
    if (!volumeId) { setChapters([]); setChapterId(''); return; }
    fetchChapters(volumeId)
      .then((list) => { setChapters(list); setChapterId(list[0]?.id ?? ''); })
      .catch(() => setChapters([]));
  }, [volumeId]);

  // provider → models
  useEffect(() => {
    fetchModels(provider)
      .then((list) => {
        setModels(list);
        setModel((prev) => (prev && list.some((m) => m.id === prev) ? prev : list[0]?.id ?? ''));
      })
      .catch(() => setModels([]));
  }, [provider]);

  // 助手：把 SSE 事件合并进 items
  const handleEvent = useCallback((event: HarnessEvent) => {
    if (event.type === 'status') { setRunning(event.status === 'running'); return; }
    if (event.type === 'turn-start') { setRunning(true); return; }
    if (event.type === 'turn-end') { setRunning(false); }
    if (event.type === 'hello') return;

    setItems((prev) => {
      const next = [...prev];
      switch (event.type) {
        case 'user-message': {
          next.push({ kind: 'user', text: event.text });
          break;
        }
        case 'text-delta': {
          const last = next[next.length - 1];
          if (last?.kind === 'assistant' && !last.done) {
            next[next.length - 1] = { ...last, text: last.text + event.text };
          } else {
            next.push({ kind: 'assistant', text: event.text, reasoning: '', thinkingOpen: true, done: false });
          }
          break;
        }
        case 'reasoning-delta': {
          const last = next[next.length - 1];
          if (last?.kind === 'assistant' && !last.done) {
            next[next.length - 1] = { ...last, reasoning: last.reasoning + event.text };
          } else {
            next.push({ kind: 'assistant', text: '', reasoning: event.text, thinkingOpen: true, done: false });
          }
          break;
        }
        case 'tool-call': {
          const tool: ToolCardState = {
            callId: event.callId,
            name: event.name,
            argsText: typeof event.arguments === 'string' ? event.arguments : JSON.stringify(event.arguments),
            args: event.arguments,
            open: event.name === 'execute',
          };
          next.push({ kind: 'tool', tool });
          break;
        }
        case 'tool-result': {
          const idx = next.findIndex((it) => it.kind === 'tool' && it.tool.callId === event.callId);
          if (idx >= 0) {
            const it = next[idx] as Extract<ChatItem, { kind: 'tool' }>;
            next[idx] = { ...it, tool: { ...it.tool, result: { success: event.success, content: event.content } } };
          }
          break;
        }
        case 'approval-request': {
          const idx = next.findIndex((it) => it.kind === 'tool' && it.tool.callId === event.callId);
          const approval: ApprovalState = { requestId: event.requestId };
          if (idx >= 0) {
            const it = next[idx] as Extract<ChatItem, { kind: 'tool' }>;
            next[idx] = { ...it, tool: { ...it.tool, approval, open: true } };
          } else if (event.callId) {
            next.push({
              kind: 'tool',
              tool: {
                callId: event.callId,
                name: event.toolName,
                argsText: event.argumentsText ?? '',
                args: parseJsonSafe(event.argumentsText),
                approval,
                open: true,
              },
            });
          }
          break;
        }
        case 'approval-answered': {
          const idx = next.findIndex((it) => it.kind === 'tool' && it.tool.approval?.requestId === event.requestId);
          if (idx >= 0) {
            const it = next[idx] as Extract<ChatItem, { kind: 'tool' }>;
            next[idx] = { ...it, tool: { ...it.tool, approval: { ...it.tool.approval!, decision: event.decision } } };
          }
          break;
        }
        case 'assistant-message': {
          const last = next[next.length - 1];
          if (last?.kind === 'assistant' && !last.done) {
            next[next.length - 1] = { ...last, text: event.text || last.text, done: true };
          } else {
            next.push({ kind: 'assistant', text: event.text, reasoning: '', thinkingOpen: false, done: true });
          }
          break;
        }
        case 'turn-end': {
          // 收尾未标记完成的 assistant
          for (let i = next.length - 1; i >= 0; i--) {
            const it = next[i];
            if (it.kind === 'assistant' && !it.done) {
              next[i] = { ...it, done: true };
              break;
            }
            if (it.kind === 'tool' && !it.tool.result) break;
          }
          break;
        }
        case 'error':
          next.push({ kind: 'system', text: `⚠ ${event.message}` });
          break;
      }
      return next;
    });
  }, []);

  const startSession = useCallback(async () => {
    if (!bookId || !model) return;
    setBusy(true);
    setLoadError('');
    try {
      const { sessionId: sid } = await createHarnessSession({
        bookId,
        chapterId: chapterId || undefined,
        provider,
        model,
      });
      setItems([]);
      setSessionId(sid);
      openEventStream(sid, handleEvent, setStreamState);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : '创建会话失败');
    } finally {
      setBusy(false);
    }
  }, [bookId, chapterId, provider, model, handleEvent]);

  const resetSession = useCallback(async () => {
    if (sessionId) {
      void closeSession(sessionId).catch((): void => undefined);
    }
    setSessionId('');
    setItems([]);
    setRunning(false);
    setStreamState('idle');
  }, [sessionId]);

  const toggleTool = useCallback((idx: number) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx || it.kind !== 'tool') return it;
      return { ...it, tool: { ...it.tool, open: !it.tool.open } };
    }));
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !sessionId) return;
    setInput('');
    try {
      await sendSessionMessage(sessionId, text);
    } catch (e) {
      setItems((prev) => [...prev, { kind: 'system', text: `⚠ 发送失败：${e instanceof Error ? e.message : e}` }]);
    }
  }, [input, sessionId]);

  const autoScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = autoScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  const streamOpen = streamState === 'open';

  return (
    <div className="hpage">
      <header className="hheader">
        <div className="hbrand">
          <span className="hlogo">🐘</span>
          小天书 × DeepSeek Harness
          <span className="hbadge">POC</span>
        </div>
        <div className="hheader-right">
          <span className={`hpill ${streamOpen ? 'hpill-ok' : ''}`}>
            {sessionId ? (streamOpen ? '已连接' : '重连中…') : '未连接'}{running ? ' · 运行中' : ''}
          </span>
          <a className="hlink" href="#/home">← 返回</a>
          {sessionId && (
            <button className="hbtn hbtn-plain" onClick={() => void resetSession()}>新会话</button>
          )}
        </div>
      </header>

      {/* 配置区（未创建会话时显示） */}
      {!sessionId && (
        <section className="hconfig">
          <h2>新建 Harness Agent 会话</h2>
          <div className="hconfig-row">
            <label>作品
              <select value={bookId} onChange={(e) => setBookId(e.target.value)}>
                {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
              </select>
            </label>
            <label>卷
              <select value={volumeId} onChange={(e) => setVolumeId(e.target.value)}>
                <option value="">（不限定）</option>
                {volumes.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
            </label>
            <label>章节
              <select value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
                <option value="">（不限定）</option>
                {chapters.map((c) => <option key={c.id} value={c.id}>{c.chapter_number}. {c.title}</option>)}
              </select>
            </label>
            <label>供应商
              <select value={provider} onChange={(e) => setProvider(e.target.value as 'deepseek' | 'openrouter')}>
                <option value="deepseek">DeepSeek</option>
                <option value="openrouter">OpenRouter</option>
              </select>
            </label>
            <label>模型
              <select value={model} onChange={(e) => setModel(e.target.value)}>
                {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
          </div>
          <div className="hconfig-actions">
            <button className="hbtn hbtn-primary" disabled={busy || !bookId || !model} onClick={() => void startSession()}>
              {busy ? '创建中…' : '启动会话'}
            </button>
            {loadError && <span className="herr">{loadError}</span>}
          </div>
          <p className="htip">
            💡 POC 演示：agent 引擎在服务端运行（DeepSeek Harness 的 agent-loop + 事件溯源会话 + 工具注册表 + 审批 seam）。
            query 直查创作数据；execute 变更会弹出审批卡并展示 diff，批准后才写库。
          </p>
        </section>
      )}

      {/* 消息区 */}
      <main className="hmessages" ref={autoScrollRef}>
        {sessionId && items.length === 0 && (
          <div className="hempty">会话已建立。试试说：「帮我看看当前章节能怎么改进」或「把第 1 章开头重写一下」</div>
        )}
        {items.map((item, idx) => {
          if (item.kind === 'user') return <div key={idx} className="hmsg hmsg-user">{item.text}</div>;
          if (item.kind === 'system') return <div key={idx} className="hmsg hmsg-system">{item.text}</div>;
          if (item.kind === 'tool') {
            return <ToolCard key={idx} item={item} sessionId={sessionId} onToggle={() => toggleTool(idx)} />;
          }
          return (
            <div key={idx} className="hmsg hmsg-assistant">
              {item.reasoning && (
                <details className="hthinking" open={item.thinkingOpen}>
                  <summary>💭 思考过程（{item.reasoning.length} 字）</summary>
                  <pre>{item.reasoning}</pre>
                </details>
              )}
              {item.text && <div className="htext">{item.text}</div>}
              {!item.done && <span className="hcaret">▊</span>}
            </div>
          );
        })}
      </main>

      {/* 输入区 */}
      <footer className="hcomposer">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={sessionId ? '输入创作指令…（回车发送，Shift+Enter 换行）' : '先选择作品与模型，然后启动会话'}
          disabled={!sessionId}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="hbtn hbtn-primary" disabled={!sessionId || !input.trim()} onClick={() => void send()}>
          发送
        </button>
      </footer>
    </div>
  );
}
